import React, { useState, useEffect } from 'react';
import { Download, ChevronRight, ArrowLeft, MessageSquare, Plus, Minus, Check } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';

/*
  Full single-file frontend:
  - Auth (login/logout), hamburger menu (admin + user), admin modals
  - Original assessment UI (landing, setup, assessment, results, detailed-scores, view-results)
  - Excel export (full implementation)
  - Uses API_BASE_URL = http://localhost:5000/api
*/

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const OPPORTUNITIES_CATEGORIES = [
  'PO',
  'Scrum Team',
  'Scrum Master',
  'Scrum Values',
  'Sprint',
  'Sprint Planning',
  'Daily Scrum',
  'Sprint Review',
  'Retrospective',
  'Backlog Management',
  'Backlog Refinement',
  'Positive Factors'
];

const SCRUM_CATEGORIES = [
  {
    name: 'Sprint Planning',
    questions: [
      'PO brings prioritized and refined Product Backlog to sprint planning',
      'Sprint planning results in a sprint plan & sprint goal',
      'Entire team Participates & commits to sprint plan',
      'When sprint planning begins sufficient stories are ready (meeting Definition of Ready) to pull into the sprint'
    ]
  },
  {
    name: 'Daily Scrum',
    questions: [
      'Problems & impediments are surfaced in daily scrum',
      'Daily Scrum is every day, same time & whole scrum team participates',
      'PO participates in daily scrum at least a few times per week',
      'Daily scrum is timeboxed (max 15-20 minutes)',
      'Each team member knows what the others are doing',
      'Team actually collaborates (vs has a status meeting) in daily scrum'
    ]
  },
  {
    name: 'Sprint Review',
    questions: [
      'Team has a Definition of Done and everyone respects (DoD)',
      'DoD achievable within each iteration',
      'Entire team periodically reviews and revises the DoD',
      'Sprint Review happens after/end of every sprint',
      'Product Backlog (Roadmap/Backlog) is updated according to feedback from reviews'
    ]
  },
  {
    name: 'Retrospective',
    questions: [
      'Retrospective happens after every sprint',
      'Retrospective results in concrete improvement proposals that get added to the backlog to get implemented',
      'Improvement proposals from prior retrospectives are reviewed',
      'Whole team + PO participates in the retrospective (Only the team attends)'
    ]
  },
  {
    name: 'Agile Reports/Charts',
    questions: [
      'Burndown charts are accurate and updated regularly through out the sprint',
      'Velocity & Capacity is maintained & considered during the planning',
      'Risks are regularly tracked and updated on ADO, and they are highlighted in management calls',
      'Milestone have been planned in advance and communicated to whole team for better planning'
    ]
  },
  {
    name: 'Backlog Mgmt',
    questions: [
      'PO has a product backlog (PBL)',
      'Top items in the PBL are prioritized and estimated',
      'PBL item are collectively estimated and agreed upon by the team (PO attends but does not estimate)',
      'Stories are estimated in relative size (story points) rather than time',
      'Top items in PBL small enough to fit in a sprint',
      'PBL contains 2-3 sprints worth of stories that meet team\'s Definition of Ready (DOR)',
      'User Stories are complete slices of functionality?',
      '"Ready" stories include Acceptance Criteria',
      'Sprint backlog is owned exclusively by the team'
    ]
  },
  {
    name: 'Backlog Refinement',
    questions: [
      'Backlog refinement is scheduled atleast twice in a sprint & whole team participates',
      'Stories are sliced based on INVEST model',
      'Testing Scope and Architecture challenges are discussed for all the required stories',
      'PO briefs about all Stories and provides clarity',
      'Ready Stories meets all the DOR checklists',
      'Use of INVEST for all the user story creation',
      'Product Owner owns the backlog refinement'
    ]
  },
  {
    name: 'Product Owner',
    questions: [
      'PO is empowered & Knowledge to prioritize',
      'PO has direct contact with team',
      'PO has direct contact with customer/stakeholders and obtains input from them'
    ]
  },
  {
    name: 'Scrum Team',
    questions: [
      'Team members sit together or use collaborative methods to collaborate in real time',
      'Team is not disrupted or controlled by outsiders',
      'Max 11 people per team (Including Scrum Master and PO)',
      'Team has all skills needed to bring backlog items to Done',
      'Each Team member clearly split the assigned stories/tasks into multiple understandable sub tasks to have better tracking and visibility',
      'Each team member knows what the others are doing and can backup any resource at any time',
      'Team members are developing T-shaped skills sufficient for all team members to fulfil any item on the backlog'
    ]
  },
  {
    name: 'Scrum Master',
    questions: [
      'Scrum Master (SM) actively coaches the team on Scrum and Agile best practices',
      'SM sits with the team or implemented practices to collaborate in real time',
      'SM actively prevents interruptions from impacting the team',
      'SM helps the team identify improvement opportunities and experiments',
      'SM helps the team track and remove impediments (escalates as appropriate)'
    ]
  },
  {
    name: 'Scrum Values',
    questions: [
      'Team members speak up if they see something wrong or have questions',
      'Team members have the courage to tackle new or difficult challenges',
      'Team members Understands & assumes collective ownership of all sprint goals',
      'Team members find ways to remove distractions and resolve internal disagreements or conflicts with the help of SM/EM if necessary',
      'Team members openly share ideas and solicit input from one another'
    ]
  },
  {
    name: 'Positive Factors',
    questions: [
      'Teams are having fun! High energy level',
      'All the Agile process has been defined and shared with the team',
      'The team has a Working agreement/Team norms',
      'Teams are discussing, critiquing and experimenting with the process',
      'Overtime work is rare and happens voluntarily on the team',
      'Measures were generated from feedback from the Happiness survey'
    ]
  }
];

const ENGINEERING_CATEGORIES = [
  {
    name: 'Development',
    questions: [
      'Architecture design primarily done by the architect in consultation with team and relevant stakeholders',
      'Architecture design is ready before the sprint planning',
      'Team utilizes internal code reviews to ensure code quality',
      'Team has effective automated unit test coverage of relevant functionality',
      'Team uses automated build functionality to enable continuous integration',
      'The team strives to adopt Test-Driven Development (TDD) techniques',
      'CI/CD is in place'
    ]
  },
  {
    name: 'Testing & Quality',
    questions: [
      'Is there a test automation framework in place?',
      'Is acceptance criteria of a story linked to the test cases related to the functionality that is changing/being added?',
      'What is the automation coverage of your End-to-end Regression suite?',
      'Are you performing early non-functional (load, stress & performance) testing?',
      'Automation scripts executed Daily?',
      'Are you practicing in-sprint automation?'
    ]
  },
  {
    name: 'Security',
    questions: [
      'Team is regularly writing AC\'s that are specifically related to security, compliance, or regulation?',
      'Architect is actively contributes to all the security documents (ex: CSA, Threat modelling etc)',
      'Team is regularly writing tests specifically around meeting these security acceptance criteria?',
      'Team is regularly factoring in the cost of security controls during high level estimations?',
      'Completion of security related documents as per milestones',
      'Team is communicating your product\'s risk classification (i.e. asset risk profile) with the team and stakeholders?'
    ]
  }
];

const HAPPINESS_CATEGORIES = [
  {
    name: 'Happiness measures',
    questions: [
      'How satisfied are you with the mood in your Scrum team?',
    ]
  },
  {
    name: 'Satisfaction level',
    questions: [
      'How satisfied are you with the collaboration in the Scrum team?',
    ]
  },
  {
    name: 'Sprint result',
    questions: [
      'How satisfied are you with the sprint result (e.g. quality and quantity)?',
    ]
  },
  {
    name: 'Scrum team workload',
    questions: [
      'How satisfied are you with the distribution of workload within your Scrum team?',
    ]
  },
  {
    name: 'Change requests',
    questions: [
      'Do we accept change requests, assess the impact and make entrepreneurial decisions?',
    ]
  },
  {
    name: 'Product increments',
    questions: [
      'Are working product increments our primary measure of progress?',
    ]
  },
  {
    name: 'Workload',
    questions: [
      'Do we ensure a healthy workload, avoid peak loads and avoid task force mode?',
    ]
  },
  {
    name: 'Focus on technical excellence',
    questions: [
      'Do we ensure sustainable development in the sense of a maintainable and expandable design and avoid technical debt? (Technical debt = consequences of poor / short-sighted technical implementation)',
    ]
  },
  {
    name: 'Consider simplicity',
    questions: [
      'Do we try to achieve the greatest possible benefit with the least possible effort? (e.g. reuse is better than new development)',
    ]
  },
  {
    name: 'Self organiced teams',
    questions: [
      'Are we as self-organized teams responsible for the implementation (how?) and the quality of the product backlog items?'
    ]
  },
];

const LoginScreen = ({
  loginEmail,
  loginPassword,
  setLoginEmail,
  setLoginPassword,
  handleLogin,
  authLoading,
  authError
}) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-white p-8">

    <h1 className="text-5xl font-extrabold text-blue-900 mb-10 tracking-wide drop-shadow-sm text-center">
      Agile Team Maturity Assessment
    </h1>

    <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
      <h2 className="text-2xl font-bold text-blue-900 mb-4">Login</h2>

      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="text-sm text-blue-800">Email</label>
          <input
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded mt-1"
            required
          />
        </div>

        <div>
          <label className="text-sm text-blue-800">Password</label>
          <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            className="w-full px-3 py-2 border rounded mt-1"
            required
          />
        </div>

        {authError && (
          <div className="text-sm text-red-600">
            {authError}
          </div>
        )}

        <button
          type="submit"
          disabled={authLoading}
          className="bg-blue-600 text-white px-4 py-2 rounded w-full"
        >
          {authLoading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  </div>
);



const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-lg p-6 w-full max-w-xl shadow-lg relative">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button onClick={onClose} className="text-gray-600">✕</button>
      </div>
      {children}
    </div>
  </div>
);


export default function AgileAssessment() {
  const [token, setToken] = useState(localStorage.getItem('am_token') || null);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('am_user') || 'null'));
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [cpOld, setCpOld] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');

  const [menuOpen, setMenuOpen] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showLinkUserProjectModal, setShowLinkUserProjectModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');

  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectId, setNewProjectId] = useState('');

  const [linkUserEmail, setLinkUserEmail] = useState('');
  const [linkProjectId, setLinkProjectId] = useState('');

  const [resetEmail, setResetEmail] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');

  const [currentStep, setCurrentStep] = useState('landing');
  const [teamName, setTeamName] = useState('');
  const [selectedTeamOption, setSelectedTeamOption] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [assessmentDate, setAssessmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [scrumRatings, setScrumRatings] = useState({});
  const [engineeringRatings, setEngineeringRatings] = useState({});
  const [happinessRatings, setHappinessRatings] = useState({});
  const [comments, setComments] = useState({});
  const [questionComments, setQuestionComments] = useState({});
  const [showCommentBox, setShowCommentBox] = useState({});
  const [expandedOpportunities, setExpandedOpportunities] = useState({});
  const [opportunityComments, setOpportunityComments] = useState({});
  const [previousAssessment, setPreviousAssessment] = useState(null);
  const [viewResultsTeam, setViewResultsTeam] = useState('');
  const [viewResultsData, setViewResultsData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Sprint Planning');
  const [assessmentSection, setAssessmentSection] = useState('scrum');

  const [allUsers, setAllUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);

  const [disabledSubtopics, setDisabledSubtopics] = useState({});

  const [highlightQuarter, setHighlightQuarter] = useState(null);

  const [showScrumSection, setShowScrumSection] = useState(true);

  const [showEngineeringSection, setShowEngineeringSection] = useState(true);

  const [showHappinessSection, setShowHappinessSection] = useState(true);




  const filteredResults = React.useMemo(() => {
    if (!viewResultsData) return [];
    if (!selectedYear) return viewResultsData;
    return viewResultsData.filter(a =>
      a.assessment_date?.startsWith(selectedYear)
    );
  }, [viewResultsData, selectedYear]);

  const toggleSubtopic = (topicName) => {
    setDisabledSubtopics(prev => {
      const updated = { ...prev, [topicName]: !prev[topicName] };

      const category = getCategoryByName(topicName);

      if (updated[topicName] === true) {
        category.questions.forEach((_, idx) => {
          const key = `${topicName}-${idx}`;
          updateRating(topicName, key, "N/A");
        });
      }

      else {
        category.questions.forEach((_, idx) => {
          const key = `${topicName}-${idx}`;
          updateRating(topicName, key, "");
        });
      }

      return updated;
    });
  };


  const ToggleButton = ({ enabled, onToggle }) => {
    return (
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 
        ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-300
          ${enabled ? 'translate-x-5' : 'translate-x-1'}`}
        />
      </button>
    );
  };




  const API_BASE_URL = "https://czm249-atma-management.azurewebsites.net/api";

  function saveAuth(tokenVal, userObj) {
    localStorage.setItem('am_token', tokenVal);
    localStorage.setItem('am_user', JSON.stringify(userObj));
    setToken(tokenVal);
    setUser(userObj);
  }
  function clearAuth() {
    localStorage.removeItem('am_token');
    localStorage.removeItem('am_user');
    setToken(null);
    setUser(null);
  }
  async function authFetch(url, opts = {}) {
    const headers = opts.headers ? { ...opts.headers } : {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const resp = await fetch(url, { ...opts, headers });
    return resp;
  }

  function handleTeamSelection(value) {
    setSelectedTeamOption(value);
    if (value === 'other') {
      setShowOtherInput(true);
      setTeamName('');
    } else {
      setShowOtherInput(false);
      setTeamName(value);
    }
  }

  function normalizeRating(v) {
    if (v === undefined || v === null || v === "") return 0;
    if (v === "N/A") return "N/A";
    return parseInt(v);
  }


  async function handleLogin(e) {
    e && e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        setAuthError(payload.error || `Login failed (${resp.status})`);
        setAuthLoading(false);
        return;
      }

      const data = await resp.json();
      if (data.success && data.token && data.user) {
        saveAuth(data.token, data.user);
        await fetchTeamsForLoggedUser(data.user.email);
        setCurrentStep('landing');
      } else {
        setAuthError('Login failed: invalid response from server');
      }
    } catch (err) {
      console.error('Login error', err);
      setAuthError('Network error while logging in');
    } finally {
      setAuthLoading(false);
    }
  }

  function handleLogout() {
    clearAuth();
    setUser(null);
    setAvailableTeams([]);
    setAllProjects([]);
    setViewResultsTeam('');
    setViewResultsData(null);
    setSelectedYear('');
    setSelectedQuarter('');
    setTeamName('');
    setAssessmentDate('');
    setScrumRatings({});
    setEngineeringRatings({});
    setHappinessRatings({});
    setDisabledSubtopics({});
    setQuestionComments({});
    setOpportunityComments({});
    setMenuOpen(false);
    setCurrentStep('landing');
  }

  async function handleAddUser(e) {
    e && e.preventDefault();
    if (!newUserEmail || !newUserPassword) return alert('Provide email and password');
    try {
      const resp = await authFetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newUserEmail, password: newUserPassword, role: newUserRole })
      });
      const payload = await resp.json();
      if (resp.ok && payload.success) {
        alert('User created');
        setNewUserEmail(''); setNewUserPassword(''); setNewUserRole('user');
        fetchAllUsers();
        setShowAddUserModal(false);
      } else {
        alert(payload.error || 'Failed to add user');
      }
    } catch (err) {
      console.error(err);
      alert('Network or server error while adding user');
    }
  }

  async function handleChangePassword(e) {
    e && e.preventDefault();
    if (!cpNew || cpNew !== cpConfirm) return alert('New password and confirm must match');
    try {
      const resp = await authFetch(`${API_BASE_URL}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, oldPassword: cpOld, newPassword: cpNew })
      });
      const payload = await resp.json();
      if (resp.ok && payload.success) {
        alert('Password changed');
        setCpOld(''); setCpNew(''); setCpConfirm('');
        setShowChangePasswordModal(false);
      } else {
        alert(payload.error || 'Failed to change password');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while changing password');
    }
  }

  async function handleResetPassword(e) {
    e && e.preventDefault();
    if (!resetEmail || !resetNewPassword) return alert('Provide email and new password');
    try {
      const resp = await authFetch(`${API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, newPassword: resetNewPassword })
      });
      const payload = await resp.json();
      if (resp.ok && payload.success) {
        alert('Password reset successful');
        setResetEmail(''); setResetNewPassword('');
        setShowResetPasswordModal(false);
      } else {
        alert(payload.error || 'Failed to reset password');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while resetting password');
    }
  }

  async function handleAddProject(e) {
    e && e.preventDefault();
    if (!newProjectName || !newProjectId) return alert('Provide project name and ID');
    try {
      const resp = await authFetch(`${API_BASE_URL}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_name: newProjectName, project_id: newProjectId })
      });
      const payload = await resp.json();
      if (resp.ok && payload.success) {
        alert('Project created');
        setNewProjectName(''); setNewProjectId('');
        fetchAllProjects();
        setShowAddProjectModal(false);
      } else {
        alert(payload.error || 'Failed to create project');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while creating project');
    }
  }

  async function handleLinkUserProject(e) {
    e && e.preventDefault();
    if (!linkUserEmail || !linkProjectId) return alert('Provide user email and project id');
    try {
      const resp = await authFetch(`${API_BASE_URL}/link-user-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: linkUserEmail, project_id: linkProjectId })
      });
      const payload = await resp.json();
      if (resp.ok && payload.success) {
        alert('User linked to project');
        setLinkUserEmail(''); setLinkProjectId('');
        setShowLinkUserProjectModal(false);
      } else {
        alert(payload.error || 'Failed to link user to project');
      }
    } catch (err) {
      console.error(err);
      alert('Network error while linking');
    }
  }

  async function fetchAllUsers() {
    try {
      const resp = await authFetch(`${API_BASE_URL}/users`);
      if (!resp.ok) return;
      const data = await resp.json();
      setAllUsers(data.users || []);
    } catch (err) {
      console.error('fetchAllUsers error', err);
    }
  }

  async function fetchAllProjects() {
    try {
      const resp = await authFetch(`${API_BASE_URL}/projects`);
      console.log("Projects API status:", resp.status);

      if (!resp.ok) {
        console.log("Projects API returned error");
        return;
      }

      const data = await resp.json();
      console.log("Projects received:", data);

      setAllProjects(data.projects || []);
    } catch (err) {
      console.error('fetchAllProjects error', err);
    }
  }


  async function fetchTeams() {
    try {
      const resp = await fetch(`${API_BASE_URL}/teams`);
      if (!resp.ok) return;
      const data = await resp.json();
      setAvailableTeams(data.teams || []);
    } catch (err) {
      console.error('fetchTeams error', err);
    }
  }

  async function fetchTeamsForLoggedUser(email) {
    try {
      if (!email) return;
      const resp = await authFetch(`${API_BASE_URL}/projects-for-user?email=${encodeURIComponent(email)}`);
      if (!resp.ok) {
        fetchTeams();
        return;
      }
      const data = await resp.json();
      setAvailableTeams(data.projects || []);
    } catch (err) {
      console.error('fetchTeamsForLoggedUser error', err);
    }
  }

  async function fetchViewResults(teamNameParam) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessments/${encodeURIComponent(teamNameParam)}`);
      if (!response.ok) {
        setViewResultsData([]);
        return;
      }

      const data = await response.json();
      const assessments = data.assessments || [];

      setViewResultsData(assessments);

      const years = [
        ...new Set(
          assessments
            .map(a => a.assessment_date?.split('-')[0])
            .filter(Boolean)
        )
      ];

      setAvailableYears(years);

      if (years.length > 0) {
        const sorted = years.sort((a, b) => b.localeCompare(a));
        setSelectedYear(sorted[0]);
      }

    } catch (error) {
      console.error('Error fetching results:', error);
      alert('Failed to fetch results. Please make sure the backend server is running.');
    }
  }


  const getPreviousQuarter = (currentQuarter) => {
    const quarterMap = { 'Q1': 'Q4', 'Q2': 'Q1', 'Q3': 'Q2', 'Q4': 'Q3' };
    return quarterMap[currentQuarter];
  };

  async function fetchPreviousAssessment() {
    const prevQuarter = getPreviousQuarter(selectedQuarter);
    try {
      const response = await fetch(`${API_BASE_URL}/assessments/${encodeURIComponent(teamName)}/${prevQuarter}`);
      if (response.ok) {
        const data = await response.json();
        setPreviousAssessment(data.assessment);
      } else {
        setPreviousAssessment(null);
      }
    } catch (error) {
      console.error('Error fetching previous assessment:', error);
      setPreviousAssessment(null);
    }
  }

  function normalizeRatings(ratings, categories, disabledSubtopics) {
    const normalized = { ...ratings };

    categories.forEach(cat => {
      const isDisabled = disabledSubtopics[cat.name] === true;

      cat.questions.forEach((_, idx) => {
        const key = `${cat.name}-${idx}`;

        if (isDisabled) {
          normalized[key] = "N/A";
          return;
        }

        if (
          normalized[key] === undefined ||
          normalized[key] === null ||
          normalized[key] === ""
        ) {
          normalized[key] = "0";
        }
      });
    });

    return normalized;
  }


  async function saveAssessment() {
    try {
      const normalizedScrum = normalizeRatings(scrumRatings, SCRUM_CATEGORIES, disabledSubtopics);
      const normalizedEng = normalizeRatings(engineeringRatings, ENGINEERING_CATEGORIES, disabledSubtopics);
      const normalizedHappy = normalizeRatings(happinessRatings, HAPPINESS_CATEGORIES, disabledSubtopics);

      const response = await fetch(`${API_BASE_URL}/assessments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team_name: teamName,
          quarter: selectedQuarter,
          assessment_date: assessmentDate,
          scrum_ratings: normalizedScrum,
          engineering_ratings: normalizedEng,
          happiness_ratings: normalizedHappy,
          question_comments: questionComments,
          opportunity_comments: opportunityComments
        })
      });

      const data = await response.json();
      if (data.success) {
        console.log('Assessment saved successfully');
        fetchTeams();
      } else {
        console.error('Error saving assessment:', data.error);
      }
    } catch (error) {
      console.error('Error saving assessment:', error);
      alert('Failed to save assessment. Please make sure the backend server is running.');
    }
  }



  function handleRatingChange(category, questionIndex, value, section = 'scrum') {
    const key = `${category}-${questionIndex}`;
    if (section === 'engineering') setEngineeringRatings({ ...engineeringRatings, [key]: value });
    else if (section === 'happiness') setHappinessRatings({ ...happinessRatings, [key]: value });
    else setScrumRatings({ ...scrumRatings, [key]: value });
  }

  function toggleCommentBox(category, questionIndex, section = 'scrum') {
    const key = `${category}-${questionIndex}-${section}`;
    setShowCommentBox({ ...showCommentBox, [key]: !showCommentBox[key] });
  }

  function handleQuestionComment(category, questionIndex, value, section = 'scrum') {
    const key = `${category}-${questionIndex}-${section}`;
    setQuestionComments({ ...questionComments, [key]: value });
  }

  function getCategoryByName(topic) {
    const all = [
      ...SCRUM_CATEGORIES,
      ...ENGINEERING_CATEGORIES,
      ...HAPPINESS_CATEGORIES
    ];
    return all.find(cat => cat.name === topic);
  }

  function updateRating(topicName, key, value) {
    if (SCRUM_CATEGORIES.some(c => c.name === topicName)) {
      setScrumRatings(prev => ({ ...prev, [key]: value }));
      return;
    }
    if (ENGINEERING_CATEGORIES.some(c => c.name === topicName)) {
      setEngineeringRatings(prev => ({ ...prev, [key]: value }));
      return;
    }
    if (HAPPINESS_CATEGORIES.some(c => c.name === topicName)) {
      setHappinessRatings(prev => ({ ...prev, [key]: value }));
      return;
    }
  }


  function getPreviousQuestionComment(category, questionIndex, section = 'scrum') {
    if (!previousAssessment || !previousAssessment.question_comments) return null;
    const key = `${category}-${questionIndex}-${section}`;
    return previousAssessment.question_comments[key] || null;
  }

  function getRadarDataForAllQuarters(section = 'scrum') {
    if (!viewResultsData) return [];

    const categories =
      section === 'engineering'
        ? ENGINEERING_CATEGORIES
        : section === 'happiness'
          ? HAPPINESS_CATEGORIES
          : SCRUM_CATEGORIES;

    return categories.map(cat => {
      const dataPoint = { category: cat.name };

      filteredResults.forEach(assessment => {
        const ratings =
          section === 'engineering'
            ? assessment.engineering_ratings
            : section === 'happiness'
              ? assessment.happiness_ratings
              : assessment.scrum_ratings;

        let total = 0;
        let count = 0;

        cat.questions.forEach((_, qIdx) => {
          const key = `${cat.name}-${qIdx}`;
          const v = ratings?.[key];
          if (v !== undefined && v !== "N/A") {
            total += parseInt(v) || 0;
            count++;
          }
        });

        dataPoint[assessment.quarter] =
          count > 0 ? (total / count).toFixed(2) : 0;
      });

      return dataPoint;
    });
  }



  function getAllOpportunitiesFromAssessments() {
    if (!viewResultsData || viewResultsData.length === 0) return {};
    const allOpportunities = {};
    viewResultsData.forEach(assessment => {
      if (assessment.opportunity_comments) {
        Object.keys(assessment.opportunity_comments).forEach(key => {
          const fullCategory = key.substring(0, key.lastIndexOf('-'));
          const fieldType = key.substring(key.lastIndexOf('-') + 1);
          if (!allOpportunities[fullCategory]) allOpportunities[fullCategory] = {};
          if (!allOpportunities[fullCategory][assessment.quarter]) allOpportunities[fullCategory][assessment.quarter] = {};
          allOpportunities[fullCategory][assessment.quarter][fieldType] = assessment.opportunity_comments[key];
        });
      }
    });
    return allOpportunities;
  }

  function isCategoryComplete(categoryName, section = 'scrum') {

    const categories =
      section === "engineering"
        ? ENGINEERING_CATEGORIES
        : section === "happiness"
          ? HAPPINESS_CATEGORIES
          : SCRUM_CATEGORIES;

    const ratings =
      section === "engineering"
        ? engineeringRatings
        : section === "happiness"
          ? happinessRatings
          : scrumRatings;

    const category = categories.find(c => c.name === categoryName);
    if (!category) return false;

    for (let idx = 0; idx < category.questions.length; idx++) {
      const key = `${categoryName}-${idx}`;
      const rating = ratings[key];

      if (rating === undefined || rating === null || rating === "") {
        return false;
      }
    }

    return true;
  }


  function toggleOpportunity(category) {
    setExpandedOpportunities({ ...expandedOpportunities, [category]: !expandedOpportunities[category] });
  }

  function handleOpportunityComment(category, field, value) {
    const key = `${category}-${field}`;
    setOpportunityComments({ ...opportunityComments, [key]: value });
  }

  function calculateSectionAverage(categories, ratings, disabled) {
    let sectionTotals = [];

    categories.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name);

      if (avg !== "N/A") {
        sectionTotals.push(parseFloat(avg));
      }
    });

    if (sectionTotals.length === 0) return "N/A";

    const result = sectionTotals.reduce((a, b) => a + b, 0) / sectionTotals.length;
    return result.toFixed(2);
  }

  function calculateOverallScore() {
    let sectionAverages = [];

    // SCRUM
    const scrumData = getScrumRadarData();
    const activeScrum = scrumData.filter(d => !isNaN(d[selectedQuarter]));
    if (activeScrum.length > 0) {
      const scrumAvg = activeScrum.reduce((sum, item) => sum + item[selectedQuarter], 0) / activeScrum.length;
      sectionAverages.push(scrumAvg);
    }

    // ENGINEERING
    const engData = getEngineeringRadarData();
    const activeEng = engData.filter(d => !isNaN(d[selectedQuarter]));
    if (activeEng.length > 0) {
      const engAvg = activeEng.reduce((sum, item) => sum + item[selectedQuarter], 0) / activeEng.length;
      sectionAverages.push(engAvg);
    }

    // HAPPINESS
    const happyData = getHappinessRadarData();
    const activeHappy = happyData.filter(d => !isNaN(d[selectedQuarter]));
    if (activeHappy.length > 0) {
      const happyAvg = activeHappy.reduce((sum, item) => sum + item[selectedQuarter], 0) / activeHappy.length;
      sectionAverages.push(happyAvg);
    }

    if (sectionAverages.length === 0) return "NaN";

    // FINAL OVERALL SCORE
    const overall = sectionAverages.reduce((a, b) => a + b, 0) / sectionAverages.length;
    return overall.toFixed(2);
  }




  function calculateCategoryTotal(categoryName, section = 'scrum') {
    const categories = section === 'engineering' ? ENGINEERING_CATEGORIES : section === 'happiness' ? HAPPINESS_CATEGORIES : SCRUM_CATEGORIES;
    const ratings = section === 'engineering' ? engineeringRatings : section === 'happiness' ? happinessRatings : scrumRatings;
    const category = categories.find(c => c.name === categoryName);
    if (!category) return 0;
    let total = 0;
    category.questions.forEach((_, idx) => {
      const key = `${categoryName}-${idx}`;
      const rating = ratings[key];
      if (rating !== undefined && rating !== 'N/A') total += parseInt(rating);
    });
    return total;
  }

  function calculateCategoryAverage(categoryName, section = "scrum") {

    if (disabledSubtopics[categoryName]) return "N/A";

    const categories =
      section === "engineering"
        ? ENGINEERING_CATEGORIES
        : section === "happiness"
          ? HAPPINESS_CATEGORIES
          : SCRUM_CATEGORIES;

    const ratings =
      section === "engineering"
        ? engineeringRatings
        : section === "happiness"
          ? happinessRatings
          : scrumRatings;

    const category = categories.find(c => c.name === categoryName);
    if (!category) return "N/A";

    let total = 0;
    let validCount = 0;
    let allNA = true;

    category.questions.forEach((_, idx) => {
      const key = `${categoryName}-${idx}`;
      const val = ratings[key];

      if (val === undefined || val === null || val === "") {
        total += 0;
        validCount++;
        allNA = false;
        return;
      }

      if (val === "N/A") return;

      const numeric = Number(val);
      if (!isNaN(numeric)) {
        total += numeric;
        validCount++;
        allNA = false;
      }
    });

    if (allNA || validCount === 0) return "N/A";

    return parseFloat((total / validCount).toFixed(2));
  }



  function calculateOverallScoreForPastAssessment(assessment) {

    function sectionAvg(categories, ratings) {
      let totals = [];

      categories.forEach(cat => {
        let total = 0, count = 0;

        cat.questions.forEach((_, qIdx) => {
          const v = ratings?.[`${cat.name}-${qIdx}`];

          if (v === "N/A") return;   // only skip N/A

          const num = parseInt(v);
          if (!isNaN(num)) {
            total += num;
            count++;
          }
        });

        if (count > 0) totals.push(total / count);
      });

      if (totals.length === 0) return "N/A";

      return (totals.reduce((a, b) => a + b, 0) / totals.length).toFixed(2);
    }

    const scrum = sectionAvg(SCRUM_CATEGORIES, assessment.scrum_ratings);
    const eng = sectionAvg(ENGINEERING_CATEGORIES, assessment.engineering_ratings);
    const happy = sectionAvg(HAPPINESS_CATEGORIES, assessment.happiness_ratings);

    const valid = [scrum, eng, happy].filter(v => v !== "N/A");

    if (valid.length === 0) return "N/A";

    return (valid.reduce((a, b) => a + parseFloat(b), 0) / valid.length).toFixed(2);
  }




  function getScrumRadarData() {
    return SCRUM_CATEGORIES.map(cat => ({ category: cat.name, [selectedQuarter]: parseFloat(calculateCategoryAverage(cat.name, 'scrum')) }));
  }
  function getEngineeringRadarData() {
    return ENGINEERING_CATEGORIES.map(cat => ({ category: cat.name, [selectedQuarter]: parseFloat(calculateCategoryAverage(cat.name, 'engineering')) }));
  }
  function getHappinessRadarData() {
    return HAPPINESS_CATEGORIES.map(cat => ({ category: cat.name, [selectedQuarter]: parseFloat(calculateCategoryAverage(cat.name, 'happiness')) }));
  }

  function downloadExcel() {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => { generateExcel(); };
    script.onerror = () => { alert('Failed to load Excel library. Please check your internet connection and try again.'); };
    document.head.appendChild(script);
  }

  function generateExcel() {
    const XLSX = window.XLSX;
    const wb = XLSX.utils.book_new();

    const instructionsData = [
      ['Agile Team Maturity Assessment'],
      ['Instructions and Guiding Principles'],
      [],
      ['Rating Scale:'],
      ['0 - Not Doing: This practice is not yet part of the team\'s process'],
      ['1 - Started: Team has been trained, enabled and is starting to do the practice, not yet consistent'],
      ['2 - Practicing: Practice is institutionalized/established'],
      ['3 - Experimenting: Team is experimenting with additional techniques to continuously improve'],
      ['4 - Advanced: Team has improvised a new and improved way of working or is sharing with other teams'],
      ['N/A: Item does not apply to this team']
    ];
    const wsInstructions = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

    const assessmentData = [
      ['Date:', assessmentDate, '', 'Team Name:', teamName],
      ['', 'Q1', 'Q2', 'Q3', 'Q4', 'Comments & Agreed Action Items'],
      []
    ];

    SCRUM_CATEGORIES.forEach(category => {
      assessmentData.push([category.name, '', '', '', '', '']);
      category.questions.forEach((question, idx) => {
        const key = `${category.name}-${idx}`;
        const commentKey = `${category.name}-${idx}-scrum`;
        const rating = scrumRatings[key] || '';
        const comment = questionComments[commentKey] || '';
        const q1Val = selectedQuarter === 'Q1' ? rating : '';
        const q2Val = selectedQuarter === 'Q2' ? rating : '';
        const q3Val = selectedQuarter === 'Q3' ? rating : '';
        const q4Val = selectedQuarter === 'Q4' ? rating : '';
        assessmentData.push([question, q1Val, q2Val, q3Val, q4Val, comment]);
      });
      assessmentData.push([]);
    });

    assessmentData.push(['Engineering Practices', '', '', '', '', '']);
    assessmentData.push([]);

    ENGINEERING_CATEGORIES.forEach(category => {
      assessmentData.push([category.name, '', '', '', '', '']);
      category.questions.forEach((question, idx) => {
        const key = `${category.name}-${idx}`;
        const commentKey = `${category.name}-${idx}-engineering`;
        const rating = engineeringRatings[key] || '';
        const comment = questionComments[commentKey] || '';
        const q1Val = selectedQuarter === 'Q1' ? rating : '';
        const q2Val = selectedQuarter === 'Q2' ? rating : '';
        const q3Val = selectedQuarter === 'Q3' ? rating : '';
        const q4Val = selectedQuarter === 'Q4' ? rating : '';
        assessmentData.push([question, q1Val, q2Val, q3Val, q4Val, comment]);
      });
      assessmentData.push([]);
    });

    assessmentData.push(['Happiness Survey', '', '', '', '', '']);
    assessmentData.push([]);

    HAPPINESS_CATEGORIES.forEach(category => {
      assessmentData.push([category.name, '', '', '', '', '']);
      category.questions.forEach((question, idx) => {
        const key = `${category.name}-${idx}`;
        const commentKey = `${category.name}-${idx}-happiness`;
        const rating = happinessRatings[key] || '';
        const comment = questionComments[commentKey] || '';
        const q1Val = selectedQuarter === 'Q1' ? rating : '';
        const q2Val = selectedQuarter === 'Q2' ? rating : '';
        const q3Val = selectedQuarter === 'Q3' ? rating : '';
        const q4Val = selectedQuarter === 'Q4' ? rating : '';
        assessmentData.push([question, q1Val, q2Val, q3Val, q4Val, comment]);
      });
      assessmentData.push([]);
    });

    const wsAssessment = XLSX.utils.aoa_to_sheet(assessmentData);
    XLSX.utils.book_append_sheet(wb, wsAssessment, 'Assessment');

    const radarData = [
      ['Date:', assessmentDate, 'Team Name:', teamName],
      [],
      ['Scrum Results'],
      ['Category', 'Q1', 'Q2', 'Q3', 'Q4']
    ];

    SCRUM_CATEGORIES.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name, 'scrum');
      const q1Val = selectedQuarter === 'Q1' ? avg : '';
      const q2Val = selectedQuarter === 'Q2' ? avg : '';
      const q3Val = selectedQuarter === 'Q3' ? avg : '';
      const q4Val = selectedQuarter === 'Q4' ? avg : '';
      radarData.push([cat.name, q1Val, q2Val, q3Val, q4Val]);
    });

    radarData.push([]);
    radarData.push(['Engineering Practices Results']);
    radarData.push(['Category', 'Q1', 'Q2', 'Q3', 'Q4']);

    ENGINEERING_CATEGORIES.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name, 'engineering');
      const q1Val = selectedQuarter === 'Q1' ? avg : '';
      const q2Val = selectedQuarter === 'Q2' ? avg : '';
      const q3Val = selectedQuarter === 'Q3' ? avg : '';
      const q4Val = selectedQuarter === 'Q4' ? avg : '';
      radarData.push([cat.name, q1Val, q2Val, q3Val, q4Val]);
    });

    radarData.push([]);
    radarData.push(['Happiness Results']);
    radarData.push(['Category', 'Q1', 'Q2', 'Q3', 'Q4']);

    HAPPINESS_CATEGORIES.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name, 'happiness');
      const q1Val = selectedQuarter === 'Q1' ? avg : '';
      const q2Val = selectedQuarter === 'Q2' ? avg : '';
      const q3Val = selectedQuarter === 'Q3' ? avg : '';
      const q4Val = selectedQuarter === 'Q4' ? avg : '';
      radarData.push([cat.name, q1Val, q2Val, q3Val, q4Val]);
    });

    radarData.push([]);
    radarData.push(['Opportunities & Actions']);
    radarData.push([]);
    radarData.push(['Category', 'Comments/Recommendations', 'Actions']);

    OPPORTUNITIES_CATEGORIES.forEach(cat => {
      const commentsVal = opportunityComments[`${cat}-comments`] || '';
      const actionsVal = opportunityComments[`${cat}-actions`] || '';
      if (commentsVal || actionsVal) {
        radarData.push([cat, commentsVal, actionsVal]);
      }
    });

    const wsRadar = XLSX.utils.aoa_to_sheet(radarData);
    XLSX.utils.book_append_sheet(wb, wsRadar, 'Radar Chart');

    const scrumRadarData = getScrumRadarData();
    const engineeringRadarData = getEngineeringRadarData();
    const happinessRadarData = getHappinessRadarData();
    const scrumAvg = (scrumRadarData.reduce((acc, curr) => acc + curr[selectedQuarter], 0) / scrumRadarData.length).toFixed(2);
    const engAvg = (engineeringRadarData.reduce((acc, curr) => acc + curr[selectedQuarter], 0) / engineeringRadarData.length).toFixed(2);
    const happinessAvg = (happinessRadarData.reduce((acc, curr) => acc + curr[selectedQuarter], 0) / happinessRadarData.length).toFixed(2);

    const resultsData = [
      ['Date:', assessmentDate],
      ['Team Name:', teamName],
      [],
      ['Scrum Average', scrumAvg],
      ['Engineering Average', engAvg],
      ['Happiness Average', happinessAvg],
      [],
      ['Scrum Category Scores'],
      ['Category', 'Q1', 'Q2', 'Q3', 'Q4', 'Average']
    ];

    SCRUM_CATEGORIES.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name, 'scrum');
      const q1Val = selectedQuarter === 'Q1' ? avg : '0.0';
      const q2Val = selectedQuarter === 'Q2' ? avg : '0.0';
      const q3Val = selectedQuarter === 'Q3' ? avg : '0.0';
      const q4Val = selectedQuarter === 'Q4' ? avg : '0.0';
      resultsData.push([cat.name, q1Val, q2Val, q3Val, q4Val, avg]);
    });

    resultsData.push([]);
    resultsData.push(['Engineering Category Scores']);
    resultsData.push(['Category', 'Q1', 'Q2', 'Q3', 'Q4', 'Average']);

    ENGINEERING_CATEGORIES.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name, 'engineering');
      const q1Val = selectedQuarter === 'Q1' ? avg : '0.0';
      const q2Val = selectedQuarter === 'Q2' ? avg : '0.0';
      const q3Val = selectedQuarter === 'Q3' ? avg : '0.0';
      const q4Val = selectedQuarter === 'Q4' ? avg : '0.0';
      resultsData.push([cat.name, q1Val, q2Val, q3Val, q4Val, avg]);
    });

    resultsData.push([]);
    resultsData.push(['Happiness Category Scores']);
    resultsData.push(['Category', 'Q1', 'Q2', 'Q3', 'Q4', 'Average']);

    HAPPINESS_CATEGORIES.forEach(cat => {
      const avg = calculateCategoryAverage(cat.name, 'happiness');
      const q1Val = selectedQuarter === 'Q1' ? avg : '0.0';
      const q2Val = selectedQuarter === 'Q2' ? avg : '0.0';
      const q3Val = selectedQuarter === 'Q3' ? avg : '0.0';
      const q4Val = selectedQuarter === 'Q4' ? avg : '0.0';
      resultsData.push([cat.name, q1Val, q2Val, q3Val, q4Val, avg]);
    });

    const wsResults = XLSX.utils.aoa_to_sheet(resultsData);
    XLSX.utils.book_append_sheet(wb, wsResults, 'Results');

    const fileName = `Agile_Assessment_${(teamName || 'team').replace(/\s+/g, '_')}_${selectedQuarter}_${assessmentDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  useEffect(() => {
    if (!user || !user.email) return;

    fetchTeamsForLoggedUser(user.email);

    console.log("AAAA");

    if (user.role === 'admin') {
      fetchAllUsers();
      fetchAllProjects();
    }
  }, [user]);


  useEffect(() => {
    if (teamName && selectedQuarter) fetchPreviousAssessment();
  }, [teamName, selectedQuarter]);

  const HamburgerMenu = () => (
    <div className="fixed top-4 right-4 z-50">
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="bg-white border rounded-md px-3 py-2 shadow hover:shadow-md flex items-center gap-2"
        title="Open menu"
      >
        <span style={{ fontSize: 18 }}>☰</span>
      </button>

      {menuOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border rounded-md shadow-lg p-3 z-50">
          <div className="mb-2 text-sm text-gray-700">
            <div><strong>{user?.email || 'Not signed in'}</strong></div>
            <div className="text-xs text-gray-500">{user?.role ? user.role.toUpperCase() : ''}</div>
          </div>

          <button
            onClick={() => {
              setCurrentStep("landing");
              setMenuOpen(false);
            }}
            className="w-full text-left py-2"
          >
            Home
          </button>

          {user && user.role === 'admin' ? (
            <>
              <button onClick={() => { setShowChangePasswordModal(true); setMenuOpen(false); }} className="w-full text-left py-2">Change Password</button>
              <button onClick={() => { setShowAddUserModal(true); setMenuOpen(false); }} className="w-full text-left py-2">Add User</button>
              <button onClick={() => { setShowAddProjectModal(true); setMenuOpen(false); }} className="w-full text-left py-2">Add Project</button>
              <button onClick={() => { setShowLinkUserProjectModal(true); setMenuOpen(false); }} className="w-full text-left py-2">Add User to Project</button>
              <button onClick={() => { setShowResetPasswordModal(true); setMenuOpen(false); }} className="w-full text-left py-2">Reset Password</button>
              <hr className="my-2" />
              <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="w-full text-left py-2 text-red-600">Logout</button>
            </>
          ) : user ? (
            <>
              <button onClick={() => { setShowChangePasswordModal(true); setMenuOpen(false); }} className="w-full text-left py-2">Change Password</button>
              <hr className="my-2" />
              <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="w-full text-left py-2 text-red-600">Logout</button>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-600 mb-2">Please login to access options</div>
            </>
          )}
        </div>
      )}
    </div>
  );

  if (!user) {
    return (
      <LoginScreen
        loginEmail={loginEmail}
        loginPassword={loginPassword}
        setLoginEmail={setLoginEmail}
        setLoginPassword={setLoginPassword}
        handleLogin={handleLogin}
        authLoading={authLoading}
        authError={authError}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <HamburgerMenu />

      {showChangePasswordModal && (
        <Modal title="Change Password" onClose={() => setShowChangePasswordModal(false)}>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div>
              <label className="text-sm">Old Password</label>
              <input type="password" value={cpOld} onChange={(e) => setCpOld(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm">New Password</label>
              <input type="password" value={cpNew} onChange={(e) => setCpNew(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm">Confirm New Password</label>
              <input type="password" value={cpConfirm} onChange={(e) => setCpConfirm(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowChangePasswordModal(false)} className="px-3 py-2 border rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {showAddUserModal && (
        <Modal title="Add User" onClose={() => setShowAddUserModal(false)}>
          <form onSubmit={handleAddUser} className="space-y-3">
            <div>
              <label className="text-sm">Email</label>
              <input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm">Password</label>
              <input type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm">Role</label>
              <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} className="w-full px-3 py-2 border rounded">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAddUserModal(false)} className="px-3 py-2 border rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
            </div>
          </form>
        </Modal>
      )}

      {showAddProjectModal && (
        <Modal title="Add Project" onClose={() => setShowAddProjectModal(false)}>
          <form onSubmit={handleAddProject} className="space-y-3">
            <div>
              <label className="text-sm">Project Name</label>
              <input type="text" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm">Project ID</label>
              <input type="text" value={newProjectId} onChange={(e) => setNewProjectId(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAddProjectModal(false)} className="px-3 py-2 border rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Create</button>
            </div>
          </form>
        </Modal>
      )}

      {showLinkUserProjectModal && (
        <Modal title="Link User to Project" onClose={() => setShowLinkUserProjectModal(false)}>
          <form onSubmit={handleLinkUserProject} className="space-y-3">
            <div>
              <label className="text-sm">User Email</label>
              <input type="email" value={linkUserEmail} onChange={(e) => setLinkUserEmail(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm font-semibold">Select Project</label>

              <select
                value={linkProjectId}
                onChange={(e) => setLinkProjectId(e.target.value)}
                className="w-full px-3 py-2 border rounded mt-1"
                required
              >
                <option value="">Select a project...</option>
                {allProjects.map((p) => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.project_name} ({p.project_id})
                  </option>
                ))}
              </select>

            </div>

            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowLinkUserProjectModal(false)} className="px-3 py-2 border rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Link</button>
            </div>
          </form>
        </Modal>
      )}

      {showResetPasswordModal && (
        <Modal title="Reset Password (Admin)" onClose={() => setShowResetPasswordModal(false)}>
          <form onSubmit={handleResetPassword} className="space-y-3">
            <div>
              <label className="text-sm">User Email</label>
              <input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div>
              <label className="text-sm">New Password</label>
              <input type="password" value={resetNewPassword} onChange={(e) => setResetNewPassword(e.target.value)} className="w-full px-3 py-2 border rounded" required />
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowResetPasswordModal(false)} className="px-3 py-2 border rounded">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Reset</button>
            </div>
          </form>
        </Modal>
      )}

      {currentStep === 'landing' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-8">
          <div className="text-center max-w-2xl">
            <h1 className="text-5xl font-bold text-blue-900 mb-6">Agile Team Maturity Assessment</h1>
            <p className="text-xl text-blue-700 mb-8">Evaluate your team's Scrum, Engineering, and Happiness metrics</p>
            <div className="flex flex-col gap-4 items-center">
              <button
                onClick={() => setCurrentStep('instructions')}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
              >
                Get Started <ChevronRight size={24} />
              </button>
              <button
                onClick={() => {
                  setCurrentStep('view-results');
                  setViewResultsTeam('');
                  setViewResultsData(null);
                  setSelectedYear('');
                }}
                className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition flex items-center gap-2"
              >
                View Results
              </button>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'instructions' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Agile Team Maturity Assessment</h2>
            <h3 className="text-2xl font-semibold text-blue-800 mb-6">Instructions and Guiding Principles</h3>
            <div className="space-y-6 text-gray-700">
              <div>
                <h3 className="text-xl font-semibold text-blue-800 mb-3">Conducting the Agile Team Maturity Assessment Recommended Process</h3>
                <div className="mb-4">
                  <p className="font-semibold text-blue-900 mb-2">Attendees</p>
                  <p>Entire Scrum Team, does not include people outside of the team such as the team manager.</p>
                </div>
                <div className="mb-4">
                  <p className="font-semibold text-blue-900 mb-2">Facilitators</p>
                  <p>Typically, the Agile Coach affiliated with the team.  The Coach and Scrum Master may agree that the Scrum Master will be the single facilitator once the team has experienced two or three assessments together.</p>
                </div>
                <div>
                  <p className="font-semibold text-blue-900 mb-2">Process</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>The team discusses each assessment factor and comes to consensus on maturity level.</li>
                    <li>Make note of areas that are most in need of improvement (i.e. prioritize).</li>
                    <li>Identify development actions when the assessment is completed, not during.</li>
                    <li>Add those development actions to the team's backlog.</li>
                  </ul>
                </div>
                <div className="mb-4">
                  <p className="font-semibold text-blue-900 mb-2">Frequency</p>
                  <p>Minimum, each quarter. If the team elects to do an assessment more often, it does not replace the usual retrospective.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-blue-800 mb-3">Assessment Guiding Principles</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The Scrum Team Assessment IS not an audit that results in traffic-light reporting for the relevant projects/scrum teams.</li>
                  <li>Assessments DO capture the team's process maturity and provide data for areas of improvement.</li>
                  <li>The rating values and comments are relative to the team's experience and rating rigor. Therefore, ratings are not a precise measurement of the team's performance. Together, the Coach and Scrum Master assess results against their knowledge of the team's day-to-day workings and barriers. Those with limited interaction with a team may form inaccurate conclusions from the numbers.</li>
                  <li>The longer the team is together, a rating norm develops . The precision of the ratings increases for both team strengths and areas of improvement. The absolute rating values continue to reflect the team's rating rigor.</li>
                  <li>Assessment results are to be shared only with relevant stakeholders.</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setCurrentStep('setup')}
              className="mt-8 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2 mx-auto"
            >
              Continue <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}

      {currentStep === 'setup' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Assessment Details</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-blue-900 mb-2">Assessment Date</label>
                <input
                  type="date"
                  value={assessmentDate}
                  onChange={(e) => setAssessmentDate(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-blue-900 mb-2">Team Name</label>
                <select
                  value={selectedTeamOption}
                  onChange={(e) => {
                    handleTeamSelection(e.target.value);
                  }}
                  className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Select a team...</option>
                  {availableTeams.map((team) => (
                    <option key={team.id || team.project_id || team.projectId} value={team.team_name || team.project_name}>
                      {team.team_name || team.project_name}
                    </option>
                  ))}
                </select>

                {showOtherInput && (
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    placeholder="Enter new team name"
                    className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none mt-3"
                  />
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-blue-900 mb-2">Select Quarter</label>
                <div className="grid grid-cols-4 gap-4">
                  {QUARTERS.map(q => (
                    <button
                      key={q}
                      onClick={() => setSelectedQuarter(q)}
                      className={`py-3 rounded-lg font-semibold transition ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-900 hover:bg-blue-200'}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => setCurrentStep('assessment')}
              disabled={!teamName}
              className="mt-8 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2 mx-auto disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Start Assessment <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}

      {currentStep === 'assessment' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
          <div className="max-w-7xl mx-auto flex gap-6">
            <div className="w-80 bg-white rounded-lg shadow-lg p-6 h-fit sticky top-8">
              <div className="mb-4">
                <h3 className="text-lg font-bold text-blue-900 mb-2">Team Assessment</h3>
                <p className="text-xs text-blue-700">Team: {teamName}</p>
                <p className="text-xs text-blue-700">Quarter: {selectedQuarter} | Date: {assessmentDate}</p>
              </div>

              <div className="space-y-6">
                <div>
                  {/* Header with Arrow */}
                  <div
                    className="flex items-center justify-between mb-2 pb-2 border-b-2 border-blue-200 cursor-pointer"
                    onClick={() => setShowScrumSection((prev) => !prev)}
                  >
                    <h4 className="text-sm font-bold text-blue-900">
                      Scrum Practices
                    </h4>

                    {/* Arrow Icon */}
                    <span
                      className={`transition-transform duration-300 ${showScrumSection ? "rotate-180" : ""
                        }`}
                    >
                      ▼
                    </span>
                  </div>

                  {/* Collapsible Content */}
                  <div
                    className="space-y-1 overflow-hidden transition-all duration-300"
                    style={{
                      maxHeight: showScrumSection ? "1000px" : "0px"
                    }}
                  >
                    {SCRUM_CATEGORIES.map((category, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-3 py-2 rounded hover:bg-blue-50 cursor-pointer"
                      >
                        <button
                          onClick={() => {
                            setSelectedCategory(category.name);
                            setAssessmentSection("scrum");
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className={`flex-1 text-left text-sm ${selectedCategory === category.name &&
                            assessmentSection === "scrum"
                            ? "text-blue-600 font-semibold"
                            : "text-gray-700"
                            }`}
                        >
                          {category.name}
                        </button>

                        {isCategoryComplete(category.name, "scrum") &&
                          !disabledSubtopics[category.name] && (
                            <Check size={18} className="text-green-600 ml-2" />
                          )}

                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!disabledSubtopics[category.name]}
                            onChange={() => toggleSubtopic(category.name)}
                          />

                          <div
                            className="
              w-11 h-6 bg-gray-300 rounded-full 
              peer-checked:bg-blue-600 
              transition-colors duration-300
            "
                          ></div>

                          <span
                            className="
              absolute left-1 top-1 h-4 w-4 bg-white rounded-full shadow 
              transition-transform duration-300
              peer-checked:translate-x-5
            "
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>


                <div>
                  {/* Header with Arrow */}
                  <div
                    className="flex items-center justify-between mb-2 pb-2 border-b-2 border-blue-200 cursor-pointer"
                    onClick={() => setShowEngineeringSection(prev => !prev)}
                  >
                    <h4 className="text-sm font-bold text-blue-900">
                      Engineering Practices
                    </h4>

                    {/* Arrow icon */}
                    <span
                      className={`transition-transform duration-300 ${showEngineeringSection ? "rotate-180" : ""
                        }`}
                    >
                      ▼
                    </span>
                  </div>

                  {/* Collapsible content */}
                  <div
                    className="space-y-1 overflow-hidden transition-all duration-300"
                    style={{
                      maxHeight: showEngineeringSection ? "1000px" : "0px"
                    }}
                  >
                    {ENGINEERING_CATEGORIES.map((category, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-3 py-2 rounded hover:bg-blue-50 cursor-pointer"
                      >
                        <button
                          onClick={() => {
                            setSelectedCategory(category.name);
                            setAssessmentSection("engineering");
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className={`flex-1 text-left text-sm ${selectedCategory === category.name &&
                            assessmentSection === "engineering"
                            ? "text-blue-600 font-semibold"
                            : "text-gray-700"
                            }`}
                        >
                          {category.name}
                        </button>

                        {isCategoryComplete(category.name, "engineering") &&
                          !disabledSubtopics[category.name] && (
                            <Check size={18} className="text-green-600 ml-2" />
                          )}

                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!disabledSubtopics[category.name]}
                            onChange={() => toggleSubtopic(category.name)}
                          />

                          <div
                            className="
              w-11 h-6 bg-gray-300 rounded-full 
              peer-checked:bg-blue-600 
              transition-colors duration-300
            "
                          ></div>

                          <span
                            className="
              absolute left-1 top-1 h-4 w-4 bg-white rounded-full shadow 
              transition-transform duration-300
              peer-checked:translate-x-5
            "
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>


                <div>
                  {/* Header + Arrow */}
                  <div
                    className="flex items-center justify-between mb-2 pb-2 border-b-2 border-blue-200 cursor-pointer"
                    onClick={() => setShowHappinessSection(prev => !prev)}
                  >
                    <h4 className="text-sm font-bold text-blue-900">
                      Happiness Survey
                    </h4>

                    <span
                      className={`transition-transform duration-300 ${showHappinessSection ? "rotate-180" : ""
                        }`}
                    >
                      ▼
                    </span>
                  </div>

                  {/* Collapsible Section */}
                  <div
                    className="space-y-1 overflow-hidden transition-all duration-300"
                    style={{ maxHeight: showHappinessSection ? "1000px" : "0px" }}
                  >
                    {HAPPINESS_CATEGORIES.map((category, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between px-3 py-2 rounded hover:bg-blue-50 cursor-pointer"
                      >
                        <button
                          onClick={() => {
                            setSelectedCategory(category.name);
                            setAssessmentSection("happiness");
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          className={`flex-1 text-left text-sm ${selectedCategory === category.name && assessmentSection === "happiness"
                            ? "text-blue-600 font-semibold"
                            : "text-gray-700"
                            }`}
                        >
                          {category.name}
                        </button>

                        {isCategoryComplete(category.name, "happiness") &&
                          !disabledSubtopics[category.name] && (
                            <Check size={18} className="text-green-600 ml-2" />
                          )}

                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={!disabledSubtopics[category.name]}
                            onChange={() => toggleSubtopic(category.name)}
                          />

                          <div
                            className="
              w-11 h-6 bg-gray-300 rounded-full 
              peer-checked:bg-blue-600 
              transition-colors duration-300
            "
                          ></div>

                          <span
                            className="
              absolute left-1 top-1 h-4 w-4 bg-white rounded-full shadow 
              transition-transform duration-300
              peer-checked:translate-x-5
            "
                          />
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              <button
                onClick={() => setCurrentStep('setup')}
                className="mt-6 w-full text-blue-600 hover:text-blue-800 flex items-center justify-center gap-2 text-sm"
              >
                <ArrowLeft size={16} /> Back to Setup
              </button>
            </div>
            <div className="flex-1 bg-white rounded-lg shadow-lg p-8">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-blue-900 mb-2">{selectedCategory}</h2>
                  <p className="text-sm text-blue-700">
                    {assessmentSection === 'scrum'
                      ? 'Scrum Practices'
                      : assessmentSection === 'engineering'
                        ? 'Engineering Practices'
                        : 'Happiness Survey'}
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-bold text-blue-900 mb-3">Rating Scale Guide</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-900 min-w-[30px]">0 -</span>
                    <span className="text-gray-700"><strong>Not Doing:</strong> This practice is not yet part of the team's process <strong>Strongly Disagree</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-900 min-w-[30px]">1 -</span>
                    <span className="text-gray-700"><strong>Started:</strong> Team has been trained, enabled and is starting to do the practice, not yet consistent <strong>Disagree</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-900 min-w-[30px]">2 -</span>
                    <span className="text-gray-700"><strong>Practicing:</strong> Practice is institutionalized /established <strong>Neither agree nor dis agree</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-900 min-w-[30px]">3 -</span>
                    <span className="text-gray-700"><strong>Experimenting:</strong> Team is experimenting with additional techniques to continuously improve <strong>Agree</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-900 min-w-[30px]">4 -</span>
                    <span className="text-gray-700"><strong>Advanced:</strong> Team has improvised a new and improved way of working or is sharing with other teams <strong>Strongly Agree</strong></span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-bold text-blue-900 min-w-[30px]">N/A -</span>
                    <span className="text-gray-700"><strong>Not Applicable:</strong> Item does not apply to this team (i.e., business team does not do engineering practices)</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                {(() => {
                  const currentCategory = assessmentSection === 'scrum' ? SCRUM_CATEGORIES.find(c => c.name === selectedCategory) :
                    assessmentSection === 'engineering' ? ENGINEERING_CATEGORIES.find(c => c.name === selectedCategory) :
                      HAPPINESS_CATEGORIES.find(c => c.name === selectedCategory);
                  if (!currentCategory) return <p className="text-sm text-gray-600">Select a category from the left.</p>;

                  return currentCategory.questions.map((question, qIdx) => (
                    <div key={qIdx}>
                      <div className="flex items-start gap-4 bg-blue-50 p-3 rounded">
                        <p className="flex-1 text-sm text-gray-700">{question}</p>
                        <div className="flex gap-2 items-center">
                          {[0, 1, 2, 3, 4, "N/A"].map(value => {
                            const ratingKey = `${currentCategory.name}-${qIdx}`;
                            const ratings =
                              assessmentSection === "scrum"
                                ? scrumRatings
                                : assessmentSection === "engineering"
                                  ? engineeringRatings
                                  : happinessRatings;

                            const isDisabled = disabledSubtopics[currentCategory.name];

                            return (
                              <button
                                key={value}
                                disabled={isDisabled}
                                onClick={() =>
                                  handleRatingChange(currentCategory.name, qIdx, value, assessmentSection)
                                }
                                className={`px-3 py-1 rounded text-sm font-semibold transition ${isDisabled
                                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                  : ratings[ratingKey] === value
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-blue-900 hover:bg-blue-100"
                                  }`}
                              >
                                {value}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => toggleCommentBox(currentCategory.name, qIdx, assessmentSection)}
                            className={`p-2 rounded transition ${showCommentBox[`${currentCategory.name}-${qIdx}-${assessmentSection}`] ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 hover:bg-blue-100'}`}
                            title="Add comment"
                          >
                            <MessageSquare size={16} />
                          </button>
                        </div>
                      </div>
                      {showCommentBox[`${currentCategory.name}-${qIdx}-${assessmentSection}`] && (
                        <div className="mt-2 ml-4">
                          {getPreviousQuestionComment(currentCategory.name, qIdx, assessmentSection) && (
                            <div className="mb-3 p-3 bg-gray-50 border border-gray-300 rounded-lg">
                              <p className="text-xs font-semibold text-gray-700 mb-1">Previous Comment ({getPreviousQuarter(selectedQuarter)}):</p>
                              <p className="text-sm text-gray-600 italic">{getPreviousQuestionComment(currentCategory.name, qIdx, assessmentSection)}</p>
                            </div>
                          )}
                          <textarea
                            value={questionComments[`${currentCategory.name}-${qIdx}-${assessmentSection}`] || ''}
                            onChange={(e) => handleQuestionComment(currentCategory.name, qIdx, e.target.value, assessmentSection)}
                            className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                            rows="2"
                            placeholder="Add your comment or action item..."
                          />
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => setCurrentStep('results')}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
                >
                  View Results <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'results' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-blue-900">Assessment Results</h2>
                  <p className="text-blue-700">Team: {teamName} | Quarter: {selectedQuarter} | Date: {assessmentDate}</p>
                </div>
                <button onClick={() => setCurrentStep('assessment')} className="text-blue-600 hover:text-blue-800 flex items-center gap-2">
                  <ArrowLeft size={20} /> Back to Assessment
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">Scrum Results</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={getScrumRadarData()}>
                      <PolarGrid stroke="#3b82f6" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#1e40af', fontSize: 10 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: '#1e40af' }} />
                      <Radar name={selectedQuarter} dataKey={selectedQuarter} stroke="#2563eb" fill="#3b82f6" fillOpacity={0.6} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">Engineering Results</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={getEngineeringRadarData()}>
                      <PolarGrid stroke="#10b981" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#065f46', fontSize: 10 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: '#065f46' }} />
                      <Radar name={selectedQuarter} dataKey={selectedQuarter} stroke="#059669" fill="#10b981" fillOpacity={0.6} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">Happiness Results</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart data={getHappinessRadarData()}>
                      <PolarGrid stroke="#f59e0b" />
                      <PolarAngleAxis dataKey="category" tick={{ fill: '#92400e', fontSize: 10 }} />
                      <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: '#92400e' }} />
                      <Radar name={selectedQuarter} dataKey={selectedQuarter} stroke="#d97706" fill="#f59e0b" fillOpacity={0.6} />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mb-8 bg-blue-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-blue-900 mb-4">Opportunities & Actions</h3>
                <div className="space-y-3">
                  {OPPORTUNITIES_CATEGORIES.map((category, idx) => (
                    <div key={idx} className="bg-white rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-4 hover:bg-blue-50 transition cursor-pointer" onClick={() => toggleOpportunity(category)}>
                        <span className="font-semibold text-blue-900">{category}</span>
                        <button className="text-blue-600 hover:text-blue-800">
                          {expandedOpportunities[category] ? <Minus size={20} /> : <Plus size={20} />}
                        </button>
                      </div>
                      {expandedOpportunities[category] && (
                        <div className="p-4 border-t border-blue-200 space-y-3">
                          <div>
                            <label className="block text-sm font-semibold text-blue-900 mb-2">Comments/Recommendations</label>
                            <textarea
                              value={opportunityComments[`${category}-comments`] || ''}
                              onChange={(e) => handleOpportunityComment(category, 'comments', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                              rows="2"
                              placeholder="Add comments or recommendations..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-blue-900 mb-2">Actions</label>
                            <textarea
                              value={opportunityComments[`${category}-actions`] || ''}
                              onChange={(e) => handleOpportunityComment(category, 'actions', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                              rows="2"
                              placeholder="Add action items..."
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={() => {
                    saveAssessment();
                    setCurrentStep('detailed-scores');
                  }}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
                >
                  Next <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'detailed-scores' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-blue-900">Detailed Scores</h2>
                  <p className="text-blue-700">Team: {teamName} | Quarter: {selectedQuarter} | Date: {assessmentDate}</p>
                </div>
                <button onClick={() => setCurrentStep('results')} className="text-blue-600 hover:text-blue-800 flex items-center gap-2">
                  <ArrowLeft size={20} /> Back
                </button>
              </div>

              <div className="mb-8 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg text-center">
                <h3 className="text-2xl font-bold mb-2">Team Score</h3>

                {(() => {
                  const score = calculateOverallScore();
                  return (
                    <>
                      <p className="text-5xl font-bold">{score}</p>
                      <p className="text-sm mt-2 opacity-90">Overall Assessment Average</p>
                    </>
                  );
                })()}
              </div>



              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-blue-800 mb-3">Scrum Categories</h4>
                    <div className="space-y-2">
                      {SCRUM_CATEGORIES.map((cat, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-blue-50 p-3 rounded">
                          <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-600">Total: <span className="font-bold text-blue-900">{calculateCategoryTotal(cat.name, 'scrum')}</span></span>
                            <span className="text-sm font-bold text-blue-900">Avg: {calculateCategoryAverage(cat.name, 'scrum')}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center bg-blue-100 p-3 rounded border-2 border-blue-300 mt-4">
                        <span className="text-sm font-bold text-blue-900">Scrum Average</span>
                        <span className="text-sm font-bold text-blue-900">{(() => {
                          const data = getScrumRadarData();
                          const active = data.filter(d => {
                            const catName = d.category;
                            return !disabledSubtopics[catName] && !isNaN(d[selectedQuarter]);
                          });

                          const avg = active.length > 0
                            ? (active.reduce((a, c) => a + c[selectedQuarter], 0) / active.length).toFixed(2)
                            : "0.00";

                          return avg;
                        })()}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-800 mb-3">Happiness Survey</h4>
                    <div className="space-y-2">
                      {HAPPINESS_CATEGORIES.map((cat, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-blue-50 p-3 rounded">
                          <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-600">Total: <span className="font-bold text-blue-900">{calculateCategoryTotal(cat.name, 'happiness')}</span></span>
                            <span className="text-sm font-bold text-blue-900">Avg: {calculateCategoryAverage(cat.name, 'happiness')}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center bg-blue-100 p-3 rounded border-2 border-blue-300 mt-4">
                        <span className="text-sm font-bold text-blue-900">Happiness Average</span>
                        <span className="text-sm font-bold text-blue-900">{(() => {
                          const data = getHappinessRadarData();
                          const active = data.filter(d => {
                            const catName = d.category;
                            return !disabledSubtopics[catName] && !isNaN(d[selectedQuarter]);
                          });

                          const avg = active.length > 0
                            ? (active.reduce((a, c) => a + c[selectedQuarter], 0) / active.length).toFixed(2)
                            : "0.00";
                          return avg;
                        })()}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-blue-800 mb-3">Engineering Categories</h4>
                    <div className="space-y-2">
                      {ENGINEERING_CATEGORIES.map((cat, idx) => (
                        <div key={idx} className="flex justify-between items-center bg-blue-50 p-3 rounded">
                          <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-xs text-gray-600">Total: <span className="font-bold text-blue-900">{calculateCategoryTotal(cat.name, 'engineering')}</span></span>
                            <span className="text-sm font-bold text-blue-900">Avg: {calculateCategoryAverage(cat.name, 'engineering')}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center bg-blue-100 p-3 rounded border-2 border-blue-300 mt-4">
                        <span className="text-sm font-bold text-blue-900">Engineering Average</span>
                        <span className="text-sm font-bold text-blue-900">{(() => {
                          const data = getEngineeringRadarData();
                          const active = data.filter(d => {
                            const catName = d.category;
                            return !disabledSubtopics[catName] && !isNaN(d[selectedQuarter]);
                          });

                          const avg = active.length > 0
                            ? (active.reduce((a, c) => a + c[selectedQuarter], 0) / active.length).toFixed(2)
                            : "0.00";

                          return avg;
                        })()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={downloadExcel}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <Download size={20} /> Download Excel Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 'view-results' && (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8">

              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-blue-900">View Assessment Results</h2>

                <button
                  onClick={() => {
                    setCurrentStep('landing');
                    setViewResultsTeam('');
                    setViewResultsData(null);
                  }}
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                >
                  <ArrowLeft size={20} /> Back to Home
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-blue-900 mb-2">
                  Select Team
                </label>
                <select
                  value={viewResultsTeam}
                  onChange={(e) => {
                    setViewResultsTeam(e.target.value);
                    if (e.target.value) fetchViewResults(e.target.value);
                  }}
                  className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Select a team...</option>
                  {availableTeams.map((team) => (
                    <option
                      key={team.id || team.project_id || team.projectId}
                      value={team.team_name || team.project_name}
                    >
                      {team.team_name || team.project_name}
                    </option>
                  ))}
                </select>
              </div>

              {availableYears.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-blue-900 mb-2">
                    Select Year
                  </label>

                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select year...</option>
                    {[...availableYears].sort().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              )}


              {(!viewResultsTeam || !viewResultsData) && (
                <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-8">
                  <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-lg shadow-lg p-8">

                      <div className="flex justify-between items-center mb-6">
                        <h2 className="text-3xl font-bold text-blue-900">View Assessment Results</h2>
                        <button
                          onClick={() => {
                            setCurrentStep('landing');
                            setViewResultsTeam('');
                            setViewResultsData(null);
                          }}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
                        >
                          <ArrowLeft size={20} /> Back to Home
                        </button>
                      </div>

                      <div className="text-center text-gray-500 py-10">
                        Please select a team to view results.
                      </div>

                    </div>
                  </div>
                </div>
              )}


              {filteredResults && filteredResults.length > 0 ? (
                <>
                  <div className="mb-6 bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <strong>Team:</strong> {viewResultsTeam} |
                      <strong className="ml-2">Quarters Available:</strong>
                      {[...new Set(filteredResults.map(a => a.quarter))].sort().join(', ')}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">
                        Scrum Results
                      </h3>

                      <ResponsiveContainer width="100%" height={400}>
                        <RadarChart data={getRadarDataForAllQuarters('scrum')}>
                          <PolarGrid stroke="#3b82f6" />
                          <PolarAngleAxis dataKey="category" tick={{ fill: "#1e40af", fontSize: 12 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: "#1e40af" }} />

                          {[...new Set(filteredResults.map(a => a.quarter))]
                            .sort()
                            .map((quarter, idx) => {
                              const colorStroke = ["#2563eb", "#10b981", "#f59e0b", "#ef4444"][idx % 4];
                              const colorFill = ["#3b82f6", "#34d399", "#fbbf24", "#f87171"][idx % 4];

                              return (
                                <Radar
                                  key={quarter}
                                  name={quarter}
                                  dataKey={quarter}
                                  stroke={colorStroke}
                                  fill={colorFill}
                                  fillOpacity={
                                    highlightQuarter === null
                                      ? 0.4
                                      : highlightQuarter === quarter
                                        ? 0.7
                                        : 0.05
                                  }
                                />
                              );
                            })}

                          <Legend
                            onMouseEnter={(e) => setHighlightQuarter(e.value)}
                            onMouseLeave={() => setHighlightQuarter(null)}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>


                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">
                        Engineering Practices Results
                      </h3>

                      <ResponsiveContainer width="100%" height={400}>
                        <RadarChart data={getRadarDataForAllQuarters('engineering')}>
                          <PolarGrid stroke="#10b981" />
                          <PolarAngleAxis dataKey="category" tick={{ fill: "#1e40af", fontSize: 12 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: "#1e40af" }} />

                          {[...new Set(filteredResults.map(a => a.quarter))]
                            .sort()
                            .map((quarter, idx) => {
                              const colorStroke = ["#2563eb", "#10b981", "#f59e0b", "#ef4444"][idx % 4];
                              const colorFill = ["#3b82f6", "#34d399", "#fbbf24", "#f87171"][idx % 4];

                              return (
                                <Radar
                                  key={quarter}
                                  name={quarter}
                                  dataKey={quarter}
                                  stroke={colorStroke}
                                  fill={colorFill}
                                  fillOpacity={
                                    highlightQuarter === null
                                      ? 0.4
                                      : highlightQuarter === quarter
                                        ? 0.7
                                        : 0.05
                                  }
                                />
                              );
                            })}

                          <Legend
                            onMouseEnter={(e) => setHighlightQuarter(e.value)}
                            onMouseLeave={() => setHighlightQuarter(null)}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>


                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-4 text-center">
                        Happiness Results
                      </h3>

                      <ResponsiveContainer width="100%" height={400}>
                        <RadarChart data={getRadarDataForAllQuarters('happiness')}>
                          <PolarGrid stroke="#f59e0b" />
                          <PolarAngleAxis dataKey="category" tick={{ fill: "#92400e", fontSize: 12 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: "#92400e" }} />

                          {[...new Set(filteredResults.map(a => a.quarter))]
                            .sort()
                            .map((quarter, idx) => {
                              const colorStroke = ["#2563eb", "#10b981", "#f59e0b", "#ef4444"][idx % 4];
                              const colorFill = ["#3b82f6", "#34d399", "#fbbf24", "#f87171"][idx % 4];

                              return (
                                <Radar
                                  key={quarter}
                                  name={quarter}
                                  dataKey={quarter}
                                  stroke={colorStroke}
                                  fill={colorFill}
                                  fillOpacity={
                                    highlightQuarter === null
                                      ? 0.4
                                      : highlightQuarter === quarter
                                        ? 0.7
                                        : 0.05
                                  }
                                />
                              );
                            })}

                          <Legend
                            onMouseEnter={(e) => setHighlightQuarter(e.value)}
                            onMouseLeave={() => setHighlightQuarter(null)}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>


                  <div className="mb-8 bg-blue-50 p-6 rounded-lg">
                    <h3 className="text-2xl font-bold text-blue-900 mb-4">
                      Opportunities & Actions (Read-Only)
                    </h3>

                    <div className="space-y-4">
                      {OPPORTUNITIES_CATEGORIES.map((category, idx) => {
                        const hasData = Object.keys(getAllOpportunitiesFromAssessments())
                          .some(key => key.startsWith(category));

                        if (!hasData) return null;

                        return (
                          <div key={idx} className="bg-white p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-3">{category}</h4>

                            {
                              [...new Set(filteredResults.map(a => a.quarter))].sort().map(quarter => {
                                const assessment = filteredResults.find(a => a.quarter === quarter);
                                if (!assessment) return null;

                                const comments = assessment.opportunity_comments?.[`${category}-comments`];
                                const actions = assessment.opportunity_comments?.[`${category}-actions`];
                                if (!comments && !actions) return null;

                                return (
                                  <div key={quarter} className="mb-3 pl-4 border-l-4 border-blue-300">
                                    <p className="text-xs font-bold text-blue-700 mb-1">{quarter}</p>

                                    {comments && (
                                      <p className="text-sm text-gray-700 mb-1">
                                        <strong>Comments:</strong> {comments}
                                      </p>
                                    )}
                                    {actions && (
                                      <p className="text-sm text-gray-700">
                                        <strong>Actions:</strong> {actions}
                                      </p>
                                    )}
                                  </div>
                                );
                              })
                            }
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-blue-900 mb-4">Detailed Scores by Quarter</h3>

                    {[...new Set(filteredResults.map(a => a.quarter))].sort().map(quarter => {
                      const assessment = filteredResults.find(a => a.quarter === quarter);
                      if (!assessment) return null;

                      const overall = calculateOverallScoreForPastAssessment(assessment);

                      return (
                        <div key={quarter}
                          className="mb-6 bg-white border-2 border-blue-200 rounded-lg p-6">

                          <h4 className="text-xl font-bold text-blue-800 mb-4">
                            {quarter} - {assessment.assessment_date}
                          </h4>

                          {/* TEAM SCORE */}
                          <div className="mb-8 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg text-center">
                            <h3 className="text-2xl font-bold mb-2">Team Score</h3>
                            <p className="text-5xl font-bold">{overall}</p>
                            <p className="text-sm mt-2 opacity-90">Overall Assessment Average</p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* SCRUM */}
                            <div>
                              <h5 className="text-lg font-semibold text-blue-700 mb-3">Scrum Categories</h5>
                              <div className="space-y-2">
                                {SCRUM_CATEGORIES.map((cat, idx) => {
                                  let total = 0, count = 0;

                                  cat.questions.forEach((_, qIdx) => {
                                    const v = assessment.scrum_ratings?.[`${cat.name}-${qIdx}`];
                                    if (v !== undefined && v !== "N/A") {
                                      total += parseInt(v) || 0;
                                      count++;
                                    }
                                  });

                                  const avg = count > 0 ? (total / count).toFixed(2) : "N/A";

                                  return (
                                    <div key={idx}
                                      className="flex justify-between items-center bg-blue-50 p-3 rounded">
                                      <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                                      <div className="flex items-center gap-4">
                                        <span className="text-xs text-gray-600">
                                          Total: <span className="font-bold text-blue-900">{total}</span>
                                        </span>
                                        <span className="text-sm font-bold text-blue-900">Avg: {avg}</span>
                                      </div>
                                    </div>
                                  );
                                })}

                                <div className="flex justify-between items-center bg-blue-100 p-3 rounded border-2 border-blue-300 mt-4">
                                  <span className="text-sm font-bold text-blue-900">Scrum Average</span>
                                  <span className="text-sm font-bold text-blue-900">
                                    {calculateOverallScoreForPastAssessment({
                                      scrum_ratings: assessment.scrum_ratings,
                                      engineering_ratings: {},
                                      happiness_ratings: {}
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* HAPPINESS */}
                            <div>
                              <h5 className="text-lg font-semibold text-blue-700 mb-3">Happiness Survey</h5>
                              <div className="space-y-2">
                                {HAPPINESS_CATEGORIES.map((cat, idx) => {
                                  let total = 0, count = 0;

                                  cat.questions.forEach((_, qIdx) => {
                                    const v = assessment.happiness_ratings?.[`${cat.name}-${qIdx}`];
                                    if (v !== undefined && v !== "N/A") {
                                      total += parseInt(v) || 0;
                                      count++;
                                    }
                                  });

                                  const avg = count > 0 ? (total / count).toFixed(2) : "N/A";

                                  return (
                                    <div key={idx}
                                      className="flex justify-between items-center bg-blue-50 p-3 rounded">
                                      <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                                      <div className="flex items-center gap-4">
                                        <span className="text-xs text-gray-600">
                                          Total: <span className="font-bold text-blue-900">{total}</span>
                                        </span>
                                        <span className="text-sm font-bold text-blue-900">Avg: {avg}</span>
                                      </div>
                                    </div>
                                  );
                                })}

                                <div className="flex justify-between items-center bg-blue-100 p-3 rounded border-2 border-blue-300 mt-4">
                                  <span className="text-sm font-bold text-blue-900">Happiness Average</span>
                                  <span className="text-sm font-bold text-blue-900">
                                    {calculateOverallScoreForPastAssessment({
                                      scrum_ratings: {},
                                      engineering_ratings: {},
                                      happiness_ratings: assessment.happiness_ratings
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* ENGINEERING */}
                            <div>
                              <h5 className="text-lg font-semibold text-blue-700 mb-3">Engineering Categories</h5>
                              <div className="space-y-2">
                                {ENGINEERING_CATEGORIES.map((cat, idx) => {
                                  let total = 0, count = 0;

                                  cat.questions.forEach((_, qIdx) => {
                                    const v = assessment.engineering_ratings?.[`${cat.name}-${qIdx}`];
                                    if (v !== undefined && v !== "N/A") {
                                      total += parseInt(v) || 0;
                                      count++;
                                    }
                                  });

                                  const avg = count > 0 ? (total / count).toFixed(2) : "N/A";

                                  return (
                                    <div key={idx}
                                      className="flex justify-between items-center bg-blue-50 p-3 rounded">
                                      <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                                      <div className="flex items-center gap-4">
                                        <span className="text-xs text-gray-600">
                                          Total: <span className="font-bold text-blue-900">{total}</span>
                                        </span>
                                        <span className="text-sm font-bold text-blue-900">Avg: {avg}</span>
                                      </div>
                                    </div>
                                  );
                                })}

                                <div className="flex justify-between items-center bg-blue-100 p-3 rounded border-2 border-blue-300 mt-4">
                                  <span className="text-sm font-bold text-blue-900">Engineering Average</span>
                                  <span className="text-sm font-bold text-blue-900">
                                    {calculateOverallScoreForPastAssessment({
                                      scrum_ratings: {},
                                      engineering_ratings: assessment.engineering_ratings,
                                      happiness_ratings: {}
                                    })}
                                  </span>
                                </div>
                              </div>
                            </div>

                          </div>

                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center text-blue-700 mt-10">
                  No results available. Please select a team.
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
}

